import { Button, Select } from "@mantine/core";
import { useState } from "react";
import type { Control } from "react-hook-form";
import type { Delivery } from "../../../../../utils/backend-types";
import type { Contract, Payment } from "../../../DeliveryOverview.types";
import { PaymentIntervalAndBillingMethod } from "../../../DeliveryOverview.types";
import "./PaymentsTab.scss";
import { PaymentsTable } from "./PaymentsTable/PaymentsTable";

const ALL_YEARS = "ALL_YEARS";

export type Value = number | string | null;

/** A `payments` entry as returned by `useFieldArray`, i.e. with its stable row id. */
export type PaymentField = Payment & { id: string };

/**
 * A payment as rendered by the table: the field array entry plus a sortable
 * `date` and its `formIndex`. The table sorts, filters and paginates its rows,
 * so `formIndex` is how a cell addresses the form field it belongs to.
 */
export type PaymentRow = PaymentField & { date: string; formIndex: number };

interface PaymentsTabProps {
  billingMethodAndPeriod: PaymentIntervalAndBillingMethod;
  control: Control<Contract>;
  deliveries: Delivery[];
  paymentsData: Array<PaymentField>;
  addPayment: (hasRlmDeliveries: boolean) => void;
}

function PaymentsTab({
  billingMethodAndPeriod,
  control,
  deliveries,
  paymentsData,
  addPayment
}: PaymentsTabProps) {
  const [yearFilter, setYearFilter] = useState<number | null>(null);

  const isMonthlyBilled =
    billingMethodAndPeriod ===
    PaymentIntervalAndBillingMethod.MonthlyBasedOnMeasurements;

  const hasRlmDeliveries =
    deliveries.find((delivery) => delivery.gridUsageBillingModel === "rlm") !==
    undefined;

  function handleAddPayment() {
    addPayment(hasRlmDeliveries);
  }

  const allYears = {
    value: ALL_YEARS,
    label: "Alle Jahre"
  };
  const years = Array.from(new Set(paymentsData.map((pd) => pd.year)))
    .sort((a, b) => b - a)
    .map((year) => ({
      value: year.toString(),
      label: year.toString()
    }));
  const allSelectItems = [allYears].concat(years);

  const paymentRows: Array<PaymentRow> = paymentsData.map(
    (payment, formIndex) => ({
      ...payment,
      formIndex,
      date: isMonthlyBilled
        ? `${payment.year}-${payment.month?.toString().padStart(2, "0")}`
        : payment.year.toString()
    })
  );
  const filteredPaymentRows = yearFilter
    ? paymentRows.filter((pd) => pd.year === yearFilter)
    : paymentRows;

  return (
    <>
      <div className="payments controls">
        {isMonthlyBilled ? (
          <Select
            data={allSelectItems}
            defaultValue={ALL_YEARS}
            onChange={(value) =>
              setYearFilter(
                value && value !== ALL_YEARS ? parseInt(value) : null
              )
            }
          />
        ) : (
          <div />
        )}
        <Button onClick={handleAddPayment}>Eintrag hinzufügen</Button>
      </div>
      <PaymentsTable
        control={control}
        isMonthlyBilled={isMonthlyBilled}
        paymentsData={filteredPaymentRows}
      />
    </>
  );
}

export { PaymentsTab, PaymentsTabProps };
